/**
 * localAuditoriaStorage.ts — Persistencia local de logs de auditoría (offline).
 *
 * QUÉ HACE: Lee/escribe logs en localStorage cuando no hay API.
 * QUÉ NO DEBE HACER: UI ni llamadas HTTP.
 */

import { logger } from "@/lib/logger";
import {
  isTransactionActive,
  txRead,
  txWrite,
} from "@/lib/persistence/transactionalStore";
import type { Log, LogFilters } from "../types/auditoria.types";
import { markRealActivityStarted } from "@/lib/localDemoOps";
import { modernizeLogs } from "./modernizeLogs";
import { AUDIT_SEED_LOGS } from "../demo/auditSeedLogs";

const LS_KEY = "auditoria_logs_v1";

/** Tope de logs conservados (los más recientes). Evita crecimiento sin límite. */
const MAX_AUDIT_LOGS = 5_000;

const SEED_LOGS = AUDIT_SEED_LOGS;

/**
 * Cola de logs pendientes de persistir. La escritura real se difiere a tiempo
 * ocioso (`requestIdleCallback`, con fallback `setTimeout`) para NO bloquear el
 * hilo justo cuando la UI debe responder al instante (p. ej. abrir el modal de
 * autorización de un movimiento o pintar la pantalla de "¡Venta Exitosa!"):
 * cada `recordLog` reescribía y comprimía el array completo de logs de forma
 * síncrona. Con `setTimeout 0` la escritura aún caía antes del foco del input
 * (que se agenda a los 80 ms), retrasando el teclado en Android; a tiempo
 * ocioso se ejecuta solo cuando el hilo está libre (o a los 2 s como tope).
 */
let auditWriteQueue: Log[] = [];
let auditFlushHandle: number | null = null;
let auditFlushIsIdle = false;

function cancelScheduledFlush(): void {
  if (auditFlushHandle == null) return;
  if (auditFlushIsIdle && typeof window.cancelIdleCallback === "function") {
    window.cancelIdleCallback(auditFlushHandle);
  } else {
    window.clearTimeout(auditFlushHandle);
  }
  auditFlushHandle = null;
  auditFlushIsIdle = false;
}

/** Escribe TODOS los logs encolados en una sola operación (orden estable). */
function flushAuditLogsNow(): void {
  if (auditWriteQueue.length === 0) return;
  const batch = auditWriteQueue;
  auditWriteQueue = [];
  cancelScheduledFlush();

  try {
    const logs = txRead<Log[] | null>(LS_KEY, null);
    const list = Array.isArray(logs) ? logs : [];
    // La cola respeta el orden de creación; al encabezar la lista (más reciente
    // primero) hay que invertir el lote para conservar la cronología exacta.
    list.unshift(...[...batch].reverse());
    if (list.length > MAX_AUDIT_LOGS) {
      list.length = MAX_AUDIT_LOGS;
    }
    txWrite(LS_KEY, list);
  } catch (err) {
    // Best-effort (igual que antes de la cola): si la cuota está llena el lote
    // se descarta con aviso; la auditoría es secundaria y no debe crecer en
    // memoria ni reintentar sin fin.
    logger.warn(
      "[Auditoría] No se pudo guardar lote de logs local (memoria llena):",
      err,
    );
  }
}

/** Fuerza el volcado de logs encolados (lecturas / pagehide / tests). */
export function flushPendingAuditLogs(): void {
  flushAuditLogsNow();
}

function scheduleAuditFlush(): void {
  if (auditFlushHandle != null) return;
  const run = () => {
    auditFlushHandle = null;
    auditFlushIsIdle = false;
    flushAuditLogsNow();
  };
  if (typeof window.requestIdleCallback === "function") {
    auditFlushIsIdle = true;
    auditFlushHandle = window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    auditFlushHandle = window.setTimeout(run, 300);
  }
}

// Flush defensivo al salir/ocultar la página para no perder logs en cola.
if (
  typeof window !== "undefined" &&
  typeof window.addEventListener === "function"
) {
  window.addEventListener("pagehide", () => {
    flushAuditLogsNow();
  });
}

export function readLocalAuditLogs(filters: Partial<LogFilters> = {}): Log[] {
  // Si hay logs encolados y alguien los lee, se persisten primero para que la
  // lectura siempre refleje lo registrado (read-your-writes del módulo).
  if (auditWriteQueue.length > 0) {
    flushAuditLogsNow();
  }
  let logs = txRead<Log[] | null>(LS_KEY, null);

  // Si no hay logs, inyectamos los de ejemplo profesional para que el usuario
  // vea el "Antes y Después" inmediatamente sin tener que hacer otra venta.
  if (!logs) {
    logs = SEED_LOGS;
    txWrite(LS_KEY, logs);
  }

  // Mapear logs antiguos "caja" a la nueva estructura especializada
  logs = modernizeLogs(logs);

  if (filters.userId) {
    logs = logs.filter((l) => l.userId === filters.userId);
  }
  if (filters.action) {
    logs = logs.filter((l) => l.action === filters.action);
  }

  // Filtro por fecha inicio — solo si se provee explícitamente
  // Si no se provee, se devuelven TODOS los logs históricos sin límite inferior
  if (filters.fechaInicio) {
    const [sy, sm, sd] = filters.fechaInicio.split("-").map(Number);
    logs = logs.filter((l) => {
      const ld = new Date(l.createdAt);
      const ly = ld.getFullYear();
      const lm = ld.getMonth() + 1;
      const lday = ld.getDate();
      if (ly > sy) return true;
      if (ly < sy) return false;
      if (lm > sm) return true;
      if (lm < sm) return false;
      return lday >= sd;
    });
  }

  // Filtro por fecha fin — solo si se provee explícitamente
  if (filters.fechaFin) {
    const [ey, em, ed] = filters.fechaFin.split("-").map(Number);
    logs = logs.filter((l) => {
      const ld = new Date(l.createdAt);
      const ly = ld.getFullYear();
      const lm = ld.getMonth() + 1;
      const lday = ld.getDate();
      if (ly < ey) return true;
      if (ly > ey) return false;
      if (lm < em) return true;
      if (lm > em) return false;
      return lday <= ed;
    });
  }

  return logs.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

export function appendLocalAuditLog(payload: {
  userId: string;
  userName: string;
  action: string;
  entidad: string;
  entidadId?: string;
  resumen?: string;
  descripcion?: string;
  severity?: "low" | "medium" | "high";
  endpoint?: string;
  sessionId?: string;
  ipAddress?: string;
  userAgent?: string;
  details?: Record<string, unknown>;
  cambios?: import("../types/auditoria.types").AuditChange[];
}): Log {
  markRealActivityStarted();

  const newLog: Log = {
    id: `AUD-${Date.now()}${auditWriteQueue.length > 0 ? `-${auditWriteQueue.length}` : ""}`,
    userId: payload.userId,
    userName: payload.userName,
    action: payload.action,
    entidad: payload.entidad,
    entidadId: payload.entidadId,
    sessionId: payload.sessionId,
    ipAddress: payload.ipAddress || "0.0.0.0",
    resumen: payload.resumen || "",
    descripcion: payload.descripcion || payload.resumen || "",
    severity: payload.severity || "medium",
    endpoint: payload.endpoint || "",
    createdAt: new Date().toISOString(),
    cambios: payload.cambios || [],
    userAgent:
      payload.userAgent ||
      (payload.details?.userAgent as string) ||
      (typeof window !== "undefined" ? window.navigator.userAgent : "Server"),
    details: payload.details || {},
    isViewed: false,
  };

  // Dentro de una transacción ACID la escritura debe ser síncrona y formar
  // parte de la transacción (si la transacción revierte, el log no queda).
  // Fuera de ella, se encola y se persiste al final del turno (no bloquea UI).
  if (isTransactionActive()) {
    // Si había cola pendiente (logs del mismo gesto) se persiste en la misma
    // transacción para no perder cronología ni atomicidad.
    flushAuditLogsNow();
    const logs = txRead<Log[] | null>(LS_KEY, null);
    const list = Array.isArray(logs) ? logs : [];
    list.unshift(newLog);
    if (list.length > MAX_AUDIT_LOGS) {
      list.length = MAX_AUDIT_LOGS;
    }
    // Escritura best-effort: la auditoría es secundaria respecto al dato de negocio.
    // Si la cuota local está llena, no debe tumbar el flujo ya completado.
    try {
      txWrite(LS_KEY, list);
    } catch (err) {
      logger.warn(
        "[Auditoría] No se pudo guardar log local (memoria llena):",
        err,
      );
    }
    return newLog;
  }

  auditWriteQueue.push(newLog);
  scheduleAuditFlush();
  return newLog;
}

/** Actualiza el estado de 'visto' de un log local */
export function updateLocalLogStatus(id: string, isViewed: boolean): void {
  const logs = txRead<Log[]>(LS_KEY, []);
  if (!Array.isArray(logs)) return;

  const updated = logs.map((l) => (l.id === id ? { ...l, isViewed } : l));
  try {
    txWrite(LS_KEY, updated);
  } catch (err) {
    logger.warn("[Auditoría] No se pudo actualizar log local:", err);
  }
}

/** Actualiza el estado de 'visto' de múltiples logs locales */
export function updateLocalLogsStatus(ids: string[], isViewed: boolean): void {
  const logs = txRead<Log[]>(LS_KEY, []);
  if (!Array.isArray(logs)) return;

  const updated = logs.map((l) =>
    ids.includes(l.id) ? { ...l, isViewed } : l,
  );
  try {
    txWrite(LS_KEY, updated);
  } catch (err) {
    logger.warn("[Auditoría] No se pudo actualizar logs locales:", err);
  }
}
